import { inputOf } from './anchors';
import { bridge } from './bridge';
import { OptionNotFound, selectOption, selectRadio, setValue, toggleCheck } from './input';
import {
  hasSearchPopup, pickFromSearchPopup, pickInOpenPopup, popupOpen, sameName, scrollerOf,
} from './popup';
import { similarity } from './hangul';
import { onScreen, onScreenAll } from './visible';
import { dismissNotices, waitForCurtain } from './dialogs';

// 행이 붙고 표가 다시 그려지기까지.
const ROW_MS = 5000;
const POLL_MS = 50;
// 셀 입력이 상태에 반영되기까지.
const CELL_MS = 3000;
// 친 값이 상태에 닿기를 기다려 주는 짧은 시간.
const SETTLE_MS = 1200;
// 탭 자료를 기다리는 시한. 서버를 한 번 다녀오는 시간이다.
const TAB_MS = 8000;
// 누른 뒤 화면이 바빠지기까지 주는 말미. 이 사이에 안 바빠지면 안 다녀오는 탭이다.
const TAB_GRACE_MS = 600;
// 상태가 이만큼 연속으로 안 움직이면 멎은 것으로 본다.
const TAB_STILL = 6;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

function press(element: HTMLElement): void {
  for (const type of ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click']) {
    element.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
  }
}

/**
 * 화면에 **보이는**, 그 글자만 가진 누를 것. 여러 개면 아래쪽(본문)을 쓴다.
 *
 * 탭을 바꿔도 다른 탭의 템플릿은 DOM 에 남는다. 안 보이는 `추가` 를 누르면 엉뚱한
 * 표에 행이 붙거나 아무 일도 일어나지 않는다 — 실제 견적서에서 본 것이다.
 */
/**
 * 단추 글자는 **사이가 벌어져 있기도 하다.** 스케쥴 등록 창의 단추는 `추 가`·`삭 제`·
 * `저 장` 이다 — 글자 그대로 견주면 못 찾고, 행이 하나도 안 붙는다.
 * 공백을 지우고 견준다.
 */
function sameLabel(text: string, label: string): boolean {
  const bare = (value: string) => value.replace(/\s+/g, '');
  return bare(text) === bare(label);
}

function findButton(label: string, within?: HTMLElement | null): HTMLElement | null {
  const root: ParentNode = within ?? document;
  const found = Array.from(root.querySelectorAll<HTMLElement>('button,div,span,p'))
    .filter((element) => element.children.length === 0
      && sameLabel(element.textContent ?? '', label))
    .filter(onScreen);
  return found.length ? found[found.length - 1] ?? null : null;
}

/**
 * 그 표가 사는 영역.
 *
 * 견적서는 탭마다 `추가` 가 있고 셋 다 보인다. 아무거나 누르면 협의사항에 행이 붙는다 —
 * 실제로 그랬다. 화면이 표 이름을 내보내면 그 영역 안에서만 단추를 찾는다.
 */
/**
 * 그 표가 있는 자리. **보이는 것을 고른다.**
 *
 * 이 ERP 는 화면을 탭으로 살려 둔다(`visible.ts` 에 적어 둔 그대로다). 그래서 앞서
 * 연 화면의 표가 DOM 에 그대로 남아 있고, 표 이름은 화면마다 겹친다 — `data` 를 쓰는
 * 화면이 여럿이다. `document.querySelector` 는 **먼저 나오는 것**을 집으므로, 뒤 탭에
 * 남은 남의 표를 이 화면 것처럼 읽게 된다.
 *
 * 실측(2026-09-21): 행 버튼을 훑는데 같은 화면이 **따로 돌리면 버튼이 보이고 이어
 * 돌리면 「칸 없음」**으로 나왔다. 읽기만 그런 것이 아니다 — `fillCell`·`fillRowCells`·
 * `pressRowButton` 이 다 이 길로 오므로, **남의 화면 표에 값을 쓸** 수 있었다.
 *
 * 하나도 안 보이면 예전처럼 넓힌다. 표를 못 찾으면 그 걸음이 죽고, 그것이 더 나쁘다.
 */
function gridArea(stateKey: string): HTMLElement | null {
  const wanted = `[data-el-grid="${stateKey}"]`;
  return onScreenAll<HTMLElement>(wanted)[0]
    ?? document.querySelector<HTMLElement>(wanted);
}

/**
 * 탭을 연다.
 *
 * 견적서등록처럼 상세가 탭으로 갈린 화면이 있다(견적항목·부품내역등록·견적이력정보).
 * 활성 탭만 그려지므로 채우려는 표의 탭을 먼저 열어야 한다.
 */
export async function openTab(title: string): Promise<void> {
  // 화면이 탭에 이름을 달아 준다(`data-el-tab`). 글자로 찾으면 아무 글자나 걸린다 —
  // 인사등록의 `첨부파일` 은 화면에 두 번 나온다.
  const anchor = Array.from(document.querySelectorAll<HTMLElement>('[data-el-tab]'))
    .find((one) => (one.getAttribute('data-el-tab') ?? '').trim() === title.trim());
  const tab = anchor ?? findButton(title);
  if (!tab) throw new Error(`탭 ${title} 을 찾지 못했다`);
  press(tab);
  // **여는 것으로 끝나지 않는다.** 탭을 누르면 화면이 그 탭 자료를 서버에서 받아 오는데,
  // 도착하기 전에 채우면 뒤늦게 온 자료가 방금 넣은 값을 덮는다. 드라이버는 그것을
  // 모르고 성공이라 답한다 — 인사등록 가족 탭에서 실제로 그랬다.
  await settleTab();
}

/**
 * 탭 자료가 도착할 때까지 기다린다.
 *
 * 화면이 바쁜지를 앱이 알려 주면 그것을 본다. 안 알려 주면 **상태가 멎을 때까지** 본다 —
 * 자료가 들어오면 배열 길이가 움직인다.
 */
async function settleTab(): Promise<void> {
  const isBusy = bridge().busy;
  const deadline = Date.now() + TAB_MS;
  if (isBusy) {
    const grace = Date.now() + TAB_GRACE_MS;
    while (!isBusy() && Date.now() < grace) await sleep(POLL_MS);
    while (isBusy() && Date.now() < deadline) await sleep(POLL_MS);
    return;
  }
  const shape = (): string => {
    const state = bridge().pageState() ?? {};
    return Object.entries(state)
      .filter(([, value]) => Array.isArray(value))
      .map(([key, value]) => `${key}=${(value as unknown[]).length}`)
      .join(' ');
  };
  let last = shape();
  let still = 0;
  while (still < TAB_STILL && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
    const now = shape();
    still = now === last ? still + 1 : 0;
    last = now;
  }
}

/**
 * 행을 더하는 단추.
 *
 * 표 안에 있는 화면도 있고 **표 바로 위 형제 영역**에 둔 화면도 있다(시간외근무보고서).
 * 표 안만 보면 못 찾고, 화면 전체를 보면 다른 표의 `추가` 를 누른다 — 견적서에서 협의사항에
 * 행이 붙은 적이 있다. 표에서 시작해 조상을 두 단계까지만 올라가며 찾는다.
 */
function findAddButton(stateKey: string, label: string): HTMLElement | null {
  const area = gridArea(stateKey);
  if (!area) return findButton(label);
  let scope: HTMLElement | null = area;
  for (let step = 0; step <= 4 && scope; step += 1) {
    const found = findButton(label, scope);
    if (found) return found;
    scope = scope.parentElement;
  }
  // 업무일지의 `추가` 는 탭 위 머리줄에 있어 표에서 한참 멀다. 가까운 데서 못 찾았다고
  // 포기하면 상세행이 하나도 안 붙는다 — 가까운 것을 **먼저** 보되, 없으면 화면 전체에서
  // 찾는다. 이 순서면 다른 표의 단추를 먼저 누르는 일은 여전히 없다.
  return findButton(label);
}

function rowsOfState(stateKey: string): Record<string, unknown>[] {
  const rows = bridge().pageState()?.[stateKey];
  return Array.isArray(rows) ? rows as Record<string, unknown>[] : [];
}

/**
 * 표의 그 행에서 그 컬럼 칸. 표 안의 칸은 화면 필드가 아니라 행·열로 지목한다.
 *
 * 한 칸에 입력이 **둘** 인 표가 있다 — 점검결과의 `처리자/보조자` 가 한 칸에 위아래로
 * 놓인다. 칸 이름으로는 위의 것밖에 못 짚으므로, 화면이 그 안에 이름을 달아 두면
 * (`elField`) 행 안에서 그 이름을 먼저 찾는다.
 */
function findCell(stateKey: string, rowIndex: number, column: string): HTMLElement | null {
  // **표 앵커가 없는 화면이 절반이다.** 실제 화면의 표 1,436개 중 `elGrid` 를 주는
  // 것은 704개뿐이다(2026-09-22 실측). 그래서 표를 못 찾는다고 그만둘 수 없다 —
  // 그러면 그 화면들에서 드라이버가 통째로 죽는다.
  //
  // 대신 넓힐 때 **보이는 행을 먼저** 고른다. 이 앱은 화면을 탭으로 살려 두므로
  // 문서 전체에서 `[data-el-row="N"]` 을 찾으면 남의 화면 행이 걸린다 — 읽기면
  // 헛것을 읽고 쓰기면 **남의 문서에 값을 친다.** `gridArea` 와 같은 규칙이다.
  const area = gridArea(stateKey);
  const wanted = `[data-el-row="${rowIndex}"]`;
  const cellIn = (row: HTMLElement | null | undefined): HTMLElement | null => (
    row?.querySelector<HTMLElement>(`[data-el-field="${column}"]`)
    ?? row?.querySelector<HTMLElement>(`[data-el-cell="${column}"]`)
    ?? null
  );
  if (area) {
    return cellIn(onScreenAll<HTMLElement>(wanted, area)[0] ?? area.querySelector<HTMLElement>(wanted));
  }
  return cellIn(unanchoredRow(stateKey, wanted, column));
}

/**
 * 표 앵커가 없을 때 **그 표의 행**을 고른다.
 *
 * 탭 속 상세 표는 앵커가 없는 일이 많다. 매입등록의 `매입상세` 표(`TableLayout`)는 `elGrid`
 * 를 안 주는데, 왼쪽 목록(`elGrid="buyList"`)도 행마다 `data-el-row` 를 달고 **먼저**
 * 나온다. 그래서 `buys` 의 1행을 찾으면 목록의 1행이 걸렸고, 그 행에도 `remark` 칸이 있어
 * 거기에 값을 쳤다 — `1행의 remark 이 화면 상태에 반영되지 않았습니다`(2026-09-25).
 * 매출등록은 목록 1행에 `pname` 이 없어 `칸을 찾지 못했다` 로 끝났다. 뿌리는 같다.
 *
 * 그래서 세 가지를 차례로 본다.
 * 1. 그 칸이 있는 행만 — 없는 행은 이 칸의 표가 아니다.
 * 2. **남의 이름표를 단 표**의 행은 뺀다 — 앵커가 `buyList` 면 `buys` 가 아니다.
 * 3. 그래도 여럿이면 행의 칸 이름이 그 상태 행의 키와 가장 많이 겹치는 것. 이 앱의
 *    `TableLayout` 은 칸 이름을 헤더의 `id` 로 달고, 그 `id` 는 대개 행 모델의 키다.
 */
function unanchoredRow(stateKey: string, wanted: string, column: string): HTMLElement | null {
  // 하나도 안 보일 때만 넓힌다 — 예전 `findCell` 의 폴백(`scope = document`)과 같은 자리다.
  const whole: ParentNode = document;
  const shown = onScreenAll<HTMLElement>(wanted);
  const all = shown.length ? shown : Array.from(whole.querySelectorAll<HTMLElement>(wanted));
  const holding = all.filter((row) => row.querySelector(
    `[data-el-field="${column}"],[data-el-cell="${column}"]`,
  ));
  if (!holding.length) return all[0] ?? null;
  const foreign = (row: HTMLElement): boolean => {
    const name = row.closest('[data-el-grid]')?.getAttribute('data-el-grid');
    return Boolean(name) && name !== stateKey;
  };
  const own = holding.filter((row) => !foreign(row));
  const pool = own.length ? own : holding;
  if (pool.length === 1) return pool[0] ?? null;
  const index = Number(/"(\d+)"/.exec(wanted)?.[1] ?? 0);
  const keys = new Set(Object.keys(rowsOfState(stateKey)[index] ?? {}));
  if (!keys.size) return pool[0] ?? null;
  const fit = (row: HTMLElement): number => {
    const names = Array.from(row.querySelectorAll('[data-el-cell]'))
      .map((cell) => cell.getAttribute('data-el-cell') ?? '')
      .filter(Boolean);
    return names.length ? names.filter((name) => keys.has(name)).length / names.length : 0;
  };
  // 동점이면 먼저 나온 것 — 예전과 같은 자리다.
  return pool.reduce((best, row) => (fit(row) > fit(best) ? row : best));
}

/**
 * **가상 스크롤 표를 그 행까지 굴린다.** 보이는 행만 DOM 에 있다.
 *
 * 입금입력 미수 목록은 11행 중 0~5행만 그렸다. 사용자가 10행을 고르자 `10행의 chk 칸을
 * 찾지 못했다` 로 끝났다(2026-09-29 벤치). 사람은 표를 굴려 그 행을 찾는다. 행 높이는
 * 그려진 행에서 재고, 그 행이 상자 가운데쯤 오게 둔다. 표 앵커가 없으면 굴리지 않는다 —
 * 남의 화면 표를 굴릴 수 있다.
 */
function scrollToRow(stateKey: string, rowIndex: number): void {
  const area = gridArea(stateKey);
  if (!area) return;
  const scroller = scrollerOf(area)
    ?? (area.scrollHeight > area.clientHeight + 8 ? area : null);
  if (!scroller) return;
  const drawn = area.querySelector<HTMLElement>('[data-el-row]');
  const height = drawn?.getBoundingClientRect().height || 25;
  scroller.scrollTop = Math.max(0, rowIndex * height - scroller.clientHeight / 2);
  scroller.dispatchEvent(new Event('scroll', { bubbles: true }));
}

/**
 * 표의 그 행 그 칸을 **화면에서** 찾는다. 안 그려져 있으면 그 행까지 굴려 본다.
 *
 * 값을 치지 않는다. 점검계획의 점검일자처럼 **고르면 바로 반영되는 칸**은 준비에서 짚기만
 * 하고 확정에서 연다 — 그 두 자리가 같은 칸을 찾아야 한다.
 */
export async function locateCell(
  stateKey: string,
  rowIndex: number,
  column: string,
): Promise<HTMLElement | null> {
  const deadline = Date.now() + ROW_MS;
  let cell = findCell(stateKey, rowIndex, column);
  if (!cell) scrollToRow(stateKey, rowIndex);
  while (!cell && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
    cell = findCell(stateKey, rowIndex, column);
  }
  return cell;
}

async function waitRowCount(stateKey: string, count: number): Promise<void> {
  const deadline = Date.now() + ROW_MS;
  while (rowsOfState(stateKey).length < count && Date.now() < deadline) {
    await sleep(POLL_MS);
  }
  if (rowsOfState(stateKey).length < count) {
    throw new Error(`${stateKey} 에 행이 늘지 않았다`);
  }
}

/**
 * 표의 한 칸을 채운다.
 *
 * 품목처럼 검색 팝업이 걸린 칸은 팝업에서 고른다 — 코드를 쳐 넣으면 품명·단위가
 * 따라오지 않는다. 금액 칸은 손대지 않는다. 수량을 넣으면 화면이 계산한다.
 */
/** 목록에서 골라야 하는 칸인가. 이 앱은 코드 칸을 `cd`·`id`·`code` 로 끝낸다. */
function looksLikeCode(column: string): boolean {
  return /(cd|id|code)$/i.test(column);
}

function sameValue(left: string, right: string): boolean {
  const [a, b] = [left.trim(), right.trim()];
  if (!a || !b) return false;
  const [x, y] = [Number(a.replace(/,/g, '')), Number(b.replace(/,/g, ''))];
  if (Number.isFinite(x) && Number.isFinite(y)) return x === y;
  return a.replace(/[^0-9A-Za-z가-힣]/g, '') === b.replace(/[^0-9A-Za-z가-힣]/g, '');
}

/**
 * 표의 한 칸을 채운다.
 *
 * 품번처럼 목록에서 고르는 칸이 있다. 여는 방법이 둘이다 — 더블클릭으로 여는 칸
 * (`bindSearchModal`)과 값을 치고 Enter 를 눌러야 여는 칸(`onEnter` 가 품목 모달을
 * 연다). 둘 다 코드만 넣어서는 품명·단가가 따라오지 않는다.
 *
 * 금액 칸은 손대지 않는다. 수량을 넣으면 화면이 계산한다.
 */
async function fillCell(
  stateKey: string,
  rowIndex: number,
  column: string,
  value: string,
): Promise<void> {
  // 행이 상태에 붙는 것과 화면에 그려지는 것은 다른 시점이다.
  const cellDeadline = Date.now() + ROW_MS;
  let cell = findCell(stateKey, rowIndex, column);
  if (!cell) scrollToRow(stateKey, rowIndex);
  while (!cell && Date.now() < cellDeadline) {
    await sleep(POLL_MS);
    cell = findCell(stateKey, rowIndex, column);
  }
  if (!cell) throw new Error(`${rowIndex + 1}행의 ${column} 칸을 찾지 못했다`);
  const read = () => String(rowsOfState(stateKey)[rowIndex]?.[column] ?? '');
  // 앵커가 감싼 입력일 수도, 앵커 자신이 입력일 수도 있다 — 화면이 `elField` 를
  // 입력에 바로 다는 곳이 있다(점검결과의 보조자 칸).
  const input = inputOf(cell);
  // 체크 칸은 값을 치는 것이 아니라 누르는 것이다 — 입금입력은 갚을 미수를 체크로
  // 고른다. 이 앱의 체크박스는 **입력 요소가 아니라 위젯**이라 종류로 알아볼 수 없다.
  // 이 위젯은 스스로 `role="check-box"` 라고 밝힌다. 클래스 이름보다 이쪽이 정확하다.
  const checkbox = cell.querySelector<HTMLElement>('[role="check-box"]')
    ?? cell.querySelector<HTMLElement>('[class*=CheckBox]');
  if (checkbox || (input instanceof HTMLInputElement && input.type === 'checkbox')) {
    const wanted = ['1', 'true', 'y', 'Y'].includes(value.trim()) ? '1' : '0';
    // Lottie 위젯이라 바깥을 눌러서는 안 먹는다. 위젯 자신을 누른다.
    // 체크가 어느 키에 담기는지는 표마다 다르다. 그 행이 바뀌었으면 눌린 것이다.
    const rowBefore = JSON.stringify(rowsOfState(stateKey)[rowIndex] ?? {});
    await toggleCheck(checkbox ?? (input as HTMLElement), read() === '1' ? '1' : '0', wanted);
    const settleBy = Date.now() + CELL_MS;
    const done = () => read() === wanted
      || JSON.stringify(rowsOfState(stateKey)[rowIndex] ?? {}) !== rowBefore;
    while (!done() && Date.now() < settleBy) await sleep(POLL_MS);
    // 위젯을 눌러도 안 바뀌는 자리가 있다(Lottie 체크박스). 화면이 열어 둔 좁은
    // 통로로 그 행만 켠다 — 임의 setState 가 아니라 체크 값 하나만 바꾸는 길이다.
    if (!done()) {
      const setRow = bridge().setRowCheck;
      if (setRow) await setRow(stateKey, rowIndex, column, wanted === '1');
      const retryBy = Date.now() + CELL_MS;
      while (!done() && Date.now() < retryBy) await sleep(POLL_MS);
    }
    if (!done()) {
      throw new Error(`${rowIndex + 1}행의 ${column} 이 화면 상태에 반영되지 않았습니다.`);
    }
    return;
  }
  if (!input) {
    // **그 행을 먼저 잡는다.** 표의 콤보는 행이 잡혀야 열린다 — `신규` 로 갓 붙인 행에
    // 곧바로 고르려 하면 목록이 안 뜨고 `그 칸에는 고를 목록이 없다` 로 끝난다.
    // 인사등록 가족 탭의 `관계` 가 그랬다. 사람도 행을 누른 뒤 고른다.
    const row = cell.closest<HTMLElement>('[data-el-row]');
    if (row) {
      press(row);
      await sleep(POLL_MS * 6);
    }
    // 표가 다시 그려지면 앞서 잡은 셀이 떨어져 나간다. 다시 잡는다.
    cell = findCell(stateKey, rowIndex, column) ?? cell;
    // **이미 그 값이면 고르지 않는다.** 당직계획의 새 줄은 구분이 `당직` 으로 나온다. 같은 것을
    // 다시 고르면 행이 안 바뀌어 아래 잣대가 「반영되지 않았다」로 던진다(2026-10-05 실측).
    if (sameValue(read(), value) || sameValue((cell.textContent ?? '').trim(), value)) return;
    // 목록에서 고르는 셀이다. 점검결과의 `결과` 칸이 그렇다(`미처리`·`처리`) — 입력칸이
    // 없다고 포기하면 이미 있는 계획을 처리로 바꿀 방법이 없다.
    // 화면이 칸에 붙인 이름과 상태의 키가 다를 수 있다 — 점검결과의 `결과` 칸은
    // `result` 인데 값은 `state` 에 담긴다. 그래서 **그 행이 바뀌었는가**로 잰다.
    const rowBefore = JSON.stringify(rowsOfState(stateKey)[rowIndex] ?? {});
    // 셀이 위젯을 여러 겹으로 감싼 화면이 있다 — 점검결과의 `결과` 칸은 콤보 아래에
    // 전송상태 글자가 또 있고, 셀을 누르면 콤보가 열리지 않는다. 콤보 자신을 집는다.
    const combo = cell.querySelector<HTMLElement>('[class*="ComboBox"][class*="view"]')
      ?? cell.querySelector<HTMLElement>('[class*=ComboBox]')
      ?? cell;
    if (cell.querySelector('[data-el-option]')) await selectRadio(cell, value);
    else await selectOption(combo, value);
    const settleBy = Date.now() + CELL_MS;
    const changed = () => JSON.stringify(rowsOfState(stateKey)[rowIndex] ?? {}) !== rowBefore;
    while (!changed() && Date.now() < settleBy) await sleep(POLL_MS);
    if (!changed()) {
      throw new Error(`${rowIndex + 1}행의 ${column} 이 화면 상태에 반영되지 않았습니다.`);
    }
    return;
  }

  const before = read();
  let picked = false;
  /** 못 고른 코드 칸에 남은 생 글자를 지운다. 남기면 그대로 저장된다. */
  const clearCell = async (): Promise<void> => {
    input.focus();
    await setValue(input, before);
    input.blur();
  };

  let seen: string[] = [];
  if (hasSearchPopup(cell)) {
    seen = await pickFromSearchPopup(cell, value);
    picked = true;
  } else {
    // `FormatNumberTextBox`(수량·금액 칸)는 입력을 **내부 state 에만** 담고 blur 에서야
    // 부모로 올린다(`onBlur` 가 `props.onChange` 를 부른다). 포커스 없이 값만 넣고
    // 떠나면 화면 상태는 0 그대로다 — 실제로 그랬다. 사람이 하는 순서를 그대로 밟는다.
    input.focus();
    await setValue(input, value);
    input.blur();
    // 값이 상태에 닿기를 기다린다. 바로 재면 아직 안 들어온 것을 "안 들어갔다" 로 읽고
    // 수량 칸에서까지 목록을 열려 한다 — `검색 팝업이 열리지 않았다: 2` 가 그것이다.
    const settleBy = Date.now() + SETTLE_MS;
    while (!sameValue(read(), value) && Date.now() < settleBy) await sleep(POLL_MS);
    // 코드 칸(`pcode`·`actcd`)은 값이 남아도 그것은 **생 글자**다 — 품명도 단가도
    // 따라오지 않는다. 목록에서 골라야 한다. 수량·비고 칸에서 Enter 를 치면 남아
    // 있던 모달을 잘못 붙잡으므로 코드 칸에서만 연다.
    if (looksLikeCode(column)) {
      input.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true,
      } as KeyboardEventInit));
      await sleep(POLL_MS * 6);
      if (popupOpen()) {
        try {
          seen = await pickInOpenPopup(input, value);
        } catch (error) {
          // 코드 입력창에 친 라벨(되묻기 답)이 검색을 오염시켜 못 맞춘 것이다. 그
          // 오염된 생 글자를 먼저 지우고, 팝업을 깨끗이 다시 열어(생 글자 없이) 그
          // 라벨로 맞춘다 — 스파이크에서 이 경로가 유일 확정에 성공했다.
          try {
            await clearCell();
            seen = await pickFromSearchPopup(cell, value);
          } catch (retryError) {
            // **친 글자를 지우고 나간다.** 코드 칸에 남은 생 글자는 품명도 단가도 없이
            // 그대로 저장된다 — 견적서에 `pcode: 도어센서`, 품명은 빈 칸으로 남았고
            // 되묻기에 답한 다음 턴도 그것을 고치지 못했다.
            await clearCell();
            // 폴백이 `OptionNotFound` 로 후보를 실어 왔으면 그것으로 되묻는다. 아니면
            // 처음 실패를 그대로 올린다. 어느 칸에서 못 골랐는지는 여기서만 안다 —
            // 이름을 붙여 던져야 다음 턴이 사용자가 고른 값을 그 칸에 도로 넣는다.
            const raised = retryError instanceof OptionNotFound ? retryError : error;
            if (raised instanceof OptionNotFound && !raised.field) raised.field = column;
            throw raised;
          }
        }
        picked = true;
      }
    }
  }

  // 넣었다고 끝이 아니다. 화면 상태에 남았을 때만 넣은 것이다.
  // 목록에서 고른 칸은 요청한 이름 대신 **코드**가 들어오므로 값이 바뀐 것으로 잰다.
  const deadline = Date.now() + CELL_MS;
  const named = () => {
    const row = rowsOfState(stateKey)[rowIndex] ?? {};
    const stem = column.replace(/(cd|id|code)$/i, '');
    for (const suffix of ['nm', 'name']) {
      const key = `${stem}${suffix}`;
      if (Object.prototype.hasOwnProperty.call(row, key)) return String(row[key] ?? '');
    }
    return null;
  };
  /**
   * **화면이 친 금액을 나눠 적었는가.** 매입등록의 `금액` 칸은 부가세여부가 `포함` 이면
   * 친 값을 공급가와 부가세로 가른다(`BuyRegister.tabAutoCalc`: `tamt = round(samt - samt/1.1)`,
   * `samt = samt - tamt`, `mijamt = 합`). 10000 을 치면 `samt 9091 · tamt 909 · mijamt 10000`
   * 이 된다 — 사람이 쳐도 그렇다. 그것을 「안 들어갔다」로 읽으면 멀쩡한 행이 실패하고,
   * 다시 치면 한 번 더 나뉜다.
   *
   * 그래서 숫자 칸은 **그 칸이 우리 입력 뒤에 바뀌었고 친 수가 같은 행 어딘가에 남았으면**
   * 들어간 것으로 본다. 행이 그대로면(입력이 씹혔으면) 여전히 실패다.
   */
  const recomputed = () => {
    if (!Number.isFinite(Number(value.replace(/,/g, '').trim()))) return false;
    const row = rowsOfState(stateKey)[rowIndex] ?? {};
    if (sameValue(read(), before) || !read().trim()) return false;
    return Object.values(row).some((cellValue) => sameValue(String(cellValue ?? ''), value));
  };
  const landed = () => {
    if (!picked && !looksLikeCode(column)) return sameValue(read(), value) || recomputed();
    // 코드 칸은 요청한 이름 대신 코드가 들어온다. 짝이 되는 이름 칸이 채워졌는지 본다.
    const name = named();
    if (name !== null) return name.trim().length > 0;
    return read().trim().length > 0 && read() !== before;
  };
  while (!landed() && Date.now() < deadline) await sleep(POLL_MS);
  // 목록에서 고른 칸은 화면이 같은 행의 다른 칸들(수량·금액)을 뒤이어 0 으로 맞춘다.
  // 그 갱신이 다 도착한 뒤에 다음 칸을 채워야 방금 넣은 값이 덮이지 않는다.
  if (picked) await sleep(SETTLE_MS);
  if (!landed() && !picked) {
    // 뒤늦은 갱신에 덮였을 수 있다. 한 번만 다시 넣는다.
    await setValue(input, value);
    const retryBy = Date.now() + CELL_MS;
    while (!landed() && Date.now() < retryBy) await sleep(POLL_MS);
  }
  if (!landed()) {
    // 코드 칸이면 친 글자가 남아 있다. 지우지 않으면 품명 없는 코드가 저장된다.
    if (looksLikeCode(column)) await clearCell();
    throw new Error(`${rowIndex + 1}행의 ${column} 이 화면 상태에 반영되지 않았습니다.`);
  }

  // **값이 들어온 것과 옳은 것이 들어온 것은 다르다.**
  //
  // 이 화면들은 검색 결과가 하나면 스스로 확정하고, 못 찾으면 목록의 첫 행을 넣기도
  // 한다. 그것을 성공으로 읽어서 `도어센서 2개` 를 부탁한 사람이 `WIRE ROPE 8mm X 2R`
  // 이 든 견적서를 받았다 — 계획도 검증도 다 통과한 채로.
  //
  // 사람은 짧게 말하고 화면은 길게 적는다(`WIRE ROPE` → `WIRE ROPE 일대일, 4본`).
  // 그래서 한쪽이 다른 쪽을 담고 있으면 같은 것으로 본다.
  if (looksLikeCode(column)) {
    const name = named();
    const filled = read().trim();
    const wrong = name !== null && name.trim()
      && !sameValue(filled, value)
      && !sameName(name, value)
      && similarity(value, name) < 0.8;
    if (wrong) {
      await clearCell();
      const error = new OptionNotFound(value, seen.slice(0, 5));
      error.field = column;
      throw error;
    }
  }
}

export type GridRowSpec = Record<string, string>;

/**
 * **이미 있는 행**의 칸을 채운다. 새 행을 만들지 않는다.
 *
 * 점검결과등록이 그렇다 — 계획은 달마다 자동으로 생겨 있고, 사람이 하는 일은 그 행의
 * 결과를 `처리` 로 바꾸고 점검일자를 적는 것뿐이다.
 */
export async function fillRowCells(options: {
  stateKey: string;
  match: Record<string, string>;
  cells: GridRowSpec;
}): Promise<number> {
  const rows = rowsOfState(options.stateKey);
  const index = rows.findIndex((row) => Object.entries(options.match).every(([key, wanted]) => (
    sameValue(String(row[key] ?? ''), wanted)
  )));
  if (index < 0) {
    throw new Error(`${options.stateKey} 에서 고칠 행을 찾지 못했다`);
  }
  for (const [column, value] of Object.entries(options.cells)) {
    if (!String(value ?? '').trim()) continue;
    // eslint-disable-next-line no-await-in-loop
    await fillCell(options.stateKey, index, column, value);
  }
  return index;
}

/**
 * 마지막 행이 **아직 아무것도 안 들어간 미저장 행**이면 그 자리를 준다.
 *
 * 숫자 칸은 화면이 `0` 으로 두므로 빈 것으로 본다. 하나라도 값이 있으면 남의 행이다.
 */
function emptyDraftRow(stateKey: string, spec: GridRowSpec): number | null {
  const rows = rowsOfState(stateKey);
  const index = rows.length - 1;
  const last = rows[index];
  if (!last || String(last.new ?? '') !== '1') return null;
  const untouched = Object.keys(spec).every((column) => {
    const value = String(last[column] ?? '').trim();
    return !value || value === '0';
  });
  return untouched ? index : null;
}

/**
 * **화면이 미리 깔아 둔 빈 뼈대 행**을 찾는다.
 *
 * 출장보고서는 신규를 누르면 `교통비·통행료·접대비·숙박비` 네 줄을 미리 깔아 준다.
 * 금액은 비어 있고 사람이 채우는 자리다. 그것을 모르고 `추가` 를 누르면 **교통비가 두
 * 줄**이 되고, 값이 0인 뼈대가 그대로 저장된다.
 *
 * 남의 값을 덮지 않는 것이 조건이다 — 우리가 쓸 칸이 이미 차 있고 그 값이 다르면
 * 그 행은 남의 행이다.
 *
 * 단 **뼈대가 기본 단가를 달고 있는 경우**는 예외다. 출장보고서의 `숙박비` 줄은
 * `uamt 50000` 을 미리 달고 수량 0으로 앉아 있다 — 문서에 아무 값도 보태지 않는
 * 자리다. 이걸 남의 행으로 보면 `숙박비` 가 두 줄이 되고 빈 줄이 남는다.
 * 그래서 수량·금액이 모두 비어 있는 행에서는 **단가 같은 계산 칸의 어긋남을 봐준다.**
 * 품명·사용내역처럼 그 행이 무엇인지 말하는 칸이 어긋나면 그대로 남의 행이다.
 */
function reusableRow(stateKey: string, spec: GridRowSpec, used: Set<number>): number | null {
  const rows = rowsOfState(stateKey);
  const blank = (value: unknown) => {
    const text = String(value ?? '').trim();
    return !text || text === '0';
  };
  const MEASURE = /^(qty|uamt|samt|tamt|amount|amt|price)$/i;
  const TALLY = /^(qty|samt|tamt|amount|amt)$/i;
  for (let index = 0; index < rows.length; index += 1) {
    if (used.has(index)) continue;
    const row = rows[index] ?? {};
    // 이 행이 문서에 보태는 것이 하나도 없으면 뼈대다.
    const idle = Object.keys(row).every((column) => !TALLY.test(column) || blank(row[column]));
    let matched = false;
    let clashes = false;
    for (const [column, value] of Object.entries(spec)) {
      if (!String(value ?? '').trim()) continue;
      const current = String(row[column] ?? '');
      if (blank(current)) continue;
      if (sameValue(current, value)) matched = true;
      else if (idle && MEASURE.test(column)) continue;
      else clashes = true;
    }
    if (matched && !clashes) return index;
  }
  return null;
}

/**
 * **방금 붙은 행이 몇 번째인가.** 누르기 전 행들과 견줘 새로 나타난 것을 찾는다.
 *
 * 대개는 맨 끝에 붙지만 **맨 앞에 붙이는 화면이 있다** — 당직계획등록의 근무자 창은 「추 가」가
 * 새 줄을 목록 맨 위에 넣는다(`[newModel, ...workerPopupList]`, 2026-10-05 실측). 끝 줄을 채우면
 * 그날 이미 있던 사람의 사원코드를 바꿔 저장한다. 하나로 정해지지 않으면 예전처럼 끝 줄이다.
 */
function freshRowIndex(stateKey: string, before: string[]): number {
  const rows = rowsOfState(stateKey);
  const left = [...before];
  const fresh: number[] = [];
  rows.forEach((row, at) => {
    const hit = left.indexOf(JSON.stringify(row));
    if (hit >= 0) left.splice(hit, 1);
    else fresh.push(at);
  });
  return fresh.length === 1 ? fresh[0]! : rows.length - 1;
}

/**
 * 표에 상세행을 채운다. 저장하지 않는다.
 *
 * `추가` 는 서버에서 빈 행 뼈대만 받아 화면 상태에 붙이는 동작이라 저장이 아니다.
 * 단가·금액은 넣지 않는다 — 품목과 수량을 넣으면 화면이 스스로 계산한다.
 */
export async function fillGridRows(options: {
  stateKey: string;
  rows: GridRowSpec[];
  tab?: string;
  addLabel?: string;
}): Promise<void> {
  const { stateKey, rows, tab } = options;
  const addLabel = options.addLabel ?? '추가';
  if (tab) await openTab(tab);

  const used = new Set<number>();
  for (const spec of rows) {
    // 품목을 못 골라 되물은 턴은 **채우다 만 행**을 남긴다. 답을 받고 또 추가하면 빈 행이
    // 그대로 쌓이고, 화면은 `수량이 0인 품목이있습니다` 로 저장을 막는다 — 사용자는
    // 자기가 만들지 않은 행을 지워야 했다. 그 자리에 이어 쓴다.
    let rowIndex = emptyDraftRow(stateKey, spec) ?? reusableRow(stateKey, spec, used);
    if (rowIndex === null) {
      const before = rowsOfState(stateKey).length;
      const shapes = rowsOfState(stateKey).map((row) => JSON.stringify(row));
      const add = findAddButton(stateKey, addLabel);
      if (!add) throw new Error(`행을 더하는 ${addLabel} 단추를 찾지 못했다`);
      press(add);
      try {
        // eslint-disable-next-line no-await-in-loop
        await waitRowCount(stateKey, before + 1);
      } catch {
        // **한 번은 씹힌다.** 문서를 고르고 바로 누르면 화면이 아직 그 문서를 읽는
        // 중이라 클릭이 삼켜진다 — `tripDetailList 에 행이 늘지 않았다` 로 끝났고,
        // 사람이 직접 누르면 멀쩡히 늘었다. 한 번 더 누른다.
        // eslint-disable-next-line no-await-in-loop
        await sleep(SETTLE_MS);
        press(add);
        // eslint-disable-next-line no-await-in-loop
        await waitRowCount(stateKey, before + 1);
      }
      rowIndex = freshRowIndex(stateKey, shapes);
    }
    used.add(rowIndex);

    for (const [column, value] of Object.entries(spec)) {
      if (!String(value ?? '').trim()) continue;
      // eslint-disable-next-line no-await-in-loop
      await fillCell(stateKey, rowIndex, column, value);
    }
  }
}

/** 눌러도 되는 시한. 창이 뜨기까지 서버를 한 번 다녀오는 화면이 있다. */
const PRESS_MS = 8000;

/**
 * **그 행의 그 칸에 달린 버튼을 누른다.**
 *
 * ERP 는 문서를 잇는 길을 표 안에 둔다 — 점검결과등록의 `점검일지` 칸에는 A4 모양
 * 아이콘이 행마다 있고, 누르면 그 점검의 일지 창이 열린다. 이런 버튼이 앱 전체에
 * 154개, 화면 60곳이다(2026-09-21 실측).
 *
 * `pressNamed` 로는 못 누른다. 그쪽은 **버튼 글자가 정확히 같을 때만** 찾는데 이 버튼들은
 * 글자가 없다. 글자가 있더라도 행마다 하나씩이라 **어느 행인지**를 못 가린다.
 *
 * 그래서 행을 값으로 짚고(`match`), 칸을 이름으로 짚는다(`column`). 칸 이름은
 * **헤더의 `id`** 다 — `TableLayout` 이 `elCell={x.id}` 로 달아 둔 그 이름이고,
 * 카탈로그의 `grids[].rowButtons[].column` 에 적힌 것과 같다. 표의 컬럼 목록에 있는
 * 이름과는 다를 수 있다(카탈로그가 칸을 셀이 읽는 필드로 접기 때문이다).
 *
 * Auto 일 때만 누른다. 창을 열고 문서를 만드는 길이라 `pressNamed` 와 같은 문이다.
 */
export async function pressRowButton(options: {
  stateKey: string;
  match: Record<string, string>;
  column: string;
  auto: boolean;
  /** 한 칸에 버튼이 여럿일 때 몇 번째인가. 안 주면 첫 번째. */
  nth?: number;
}): Promise<{ row: number; notices: string[] }> {
  if (!options.auto) {
    throw new Error(`${options.column} 버튼은 Auto 모드가 켜져 있을 때만 누를 수 있다`);
  }
  const rows = rowsOfState(options.stateKey);
  if (!rows.length) {
    // **조회를 안 하고 누르면 여기로 온다.** 「행을 못 찾았다」와 「표가 비었다」는
    // 고치는 방법이 다르다 — 앞은 이름이 틀린 것이고 뒤는 순서가 틀린 것이다.
    throw new Error(`${options.stateKey} 표가 비었다. 누르기 전에 먼저 조회해야 한다`);
  }
  const hits = (test: (row: Record<string, unknown>, key: string, wanted: string) => boolean) => rows
    .map((row, at) => ({ row, at }))
    .filter(({ row }) => Object.entries(options.match).every(([key, wanted]) => test(row, key, wanted)));
  // 먼저 **정확히** 같은 행을 찾는다.
  let found = hits((row, key, wanted) => sameValue(String(row[key] ?? ''), wanted));
  if (!found.length) {
    // **사람이 부르는 이름과 화면의 이름이 다르다.** 「김영자빌딩」이라고 말하는데
    // 표에는 「김영자빌딩(대성빌딩)」으로 들어 있다(실측 2026-09-21). 앞부분이 겹치면
    // 같은 곳으로 본다 — `sameName` 이 팝업에서 이미 쓰는 규칙이다.
    //
    // **여럿이면 안 누른다.** 누르는 것은 그 행의 문서를 여는 일이라, 아무거나 고르면
    // 사람은 엉뚱한 문서를 본 줄도 모른다. 무엇이 걸렸는지 말하고 물러난다.
    found = hits((row, key, wanted) => sameName(String(row[key] ?? ''), wanted));
    if (found.length > 1) {
      const which = found.slice(0, 5)
        .map(({ row }) => Object.keys(options.match).map((key) => String(row[key] ?? '')).join(' '))
        .join(' / ');
      throw new Error(`누를 행이 ${found.length}개다. 어느 것인지 알려 달라: ${which}`);
    }
  }
  const index = found[0]?.at ?? -1;
  if (index < 0) {
    throw new Error(`${options.stateKey} 에서 누를 행을 찾지 못했다`);
  }
  const cell = findCell(options.stateKey, index, options.column);
  if (!cell) {
    throw new Error(`${options.column} 칸이 그 행에 없다`);
  }
  // 화면에 보이는 것만 센다. 숨은 칸에도 버튼이 남아 있는 표가 있다.
  const buttons = Array.from(cell.querySelectorAll<HTMLElement>('button')).filter(onScreen);
  const button = buttons[options.nth ?? 0];
  if (!button) {
    throw new Error(`${options.column} 칸에 누를 수 있는 버튼이 없다`);
  }
  await waitForCurtain(Date.now() + PRESS_MS);
  // 앞서 떠 있던 알림은 이번 누름의 결과가 아니다. 먼저 치우고 **그 뒤에** 뜬 것만 센다.
  await dismissNotices();
  press(button);
  const notices = await dismissNotices(3, 1200);
  return { row: index, notices };
}

/** 바닥줄의 `총 2,176행` 에서 수를 읽는 자. */
const TOTAL_TEXT = /^\s*총\s*([\d,]+)\s*행/;

/**
 * **표 바닥줄이 말하는 총 행 수.** 없으면 `null`.
 *
 * 이 ERP 의 표는 바닥줄에 `총 N행` 을 그린다. 나눠 보여 주는 목록(`InfinityRetrieve`)이면
 * 그 수는 화면이 **받은 전체**이고, 상태의 행 배열은 **지금까지 펼친 것**뿐이다 —
 * 점검결과등록 9월 계획은 바닥줄이 `총 53행`, 상태는 50행이었다(실제 테넌트, 2026-10-06).
 * 사람은 이 수를 보고 끝까지 내려 본다.
 *
 * 표 영역 안에 바닥줄이 없으면(GridLayout 은 바닥줄을 표 영역 **바로 뒤**에 둔다) 바로 뒤
 * 형제 두 개까지만 본다. 더 넓히면 이웃 표의 바닥줄을 읽는다.
 */
export function listTotal(stateKey: string): number | null {
  const area = gridArea(stateKey);
  if (!area) return null;
  const places: Element[] = [area];
  let next = area.nextElementSibling;
  for (let hop = 0; next && hop < 2; hop += 1, next = next.nextElementSibling) places.push(next);
  for (const place of places) {
    const found = Array.from(place.querySelectorAll<HTMLElement>('div'))
      .map((element) => TOTAL_TEXT.exec(element.textContent ?? ''))
      .find(Boolean);
    if (found) return Number(found[1]!.replace(/,/g, ''));
  }
  return null;
}

// 한 번 굴린 뒤 다음 쪽이 붙기를 기다리는 시간. 다음 쪽은 서버를 안 다녀온다 — 화면이
// 이미 받은 것을 50행씩 잘라 붙인다(`InfinityRetrieve.retrieve`).
const PAGE_MS = 3000;
// 기다리는 동안 끝으로 다시 보내는 간격(`POLL_MS` 몇 번마다).
const NUDGE_EVERY = 4;

/**
 * **나눠 보여 주는 목록을 끝까지 펼친다.** 사람이 표를 끝까지 내리는 것과 같다.
 *
 * 화면은 서버에서 전부 받고도 50행만 상태에 올리고, 표를 끝까지 내리면 50행씩 더 붙인다.
 * 드라이버는 상태를 읽으므로 그대로 두면 첫 50행만 가져간다 — 점검결과등록 10월 계획이
 * 바닥줄 `총 2,176행` 인데 50행으로 답했다(실제 테넌트, 2026-10-06). 바닥줄의 총 수에
 * 닿거나, 몇 번 굴려도 `PAGE_MS` 동안 더 안 붙거나, 마감이 될 때까지 굴린다. 다 펼치면
 * 맨 위로 되돌린다.
 *
 * 펼친 행 수를 돌려준다.
 */
export async function unfoldList(stateKey: string, deadline: number): Promise<number> {
  const count = () => rowsOfState(stateKey).length;
  const total = listTotal(stateKey);
  if (total === null || count() >= total) return count();
  const area = gridArea(stateKey);
  if (!area) return count();
  const scrollers = (): HTMLElement[] => {
    const lists = Array.from(area.querySelectorAll<HTMLElement>('[class*=ReactVirtualized__Grid]'));
    if (lists.length) return lists;
    const one = scrollerOf(area);
    return one ? [one] : [];
  };
  if (!scrollers().length) return count();
  const toBottom = () => {
    for (const scroller of scrollers()) {
      scroller.scrollTop = scroller.scrollHeight;
      scroller.dispatchEvent(new Event('scroll', { bubbles: true }));
    }
  };
  while (count() < total && Date.now() < deadline) {
    const before = count();
    const until = Math.min(deadline, Date.now() + PAGE_MS);
    // **붙은 직후에는 표 높이가 아직 옛것이다.** 그때 끝으로 보내면 제자리라 스크롤이 안
    // 일어나고 다음 쪽도 안 온다 — 한 쪽에 2.2초씩 걸렸다(실제 테넌트, 2026-10-06).
    // 기다리는 동안 몇 번 더 끝으로 보낸다.
    let nudge = 0;
    while (count() === before && Date.now() < until) {
      if (nudge % NUDGE_EVERY === 0) toBottom();
      nudge += 1;
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
    }
    if (count() === before) break;
  }
  for (const scroller of scrollers()) {
    scroller.scrollTop = 0;
    scroller.dispatchEvent(new Event('scroll', { bubbles: true }));
  }
  return count();
}
